import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';

import { Button, Sheet, TextField } from '@/components/ui';
import { upsertProductoPetshop } from '@/services/firebase/petshops';
import { usePetshopsMeta } from '@/hooks/usePetshopsMeta';
import { colors, typography } from '@/theme/tokens';
import { type PetshopId, type PetshopProducto } from '@/types/domain';

interface Props {
  visible: boolean;
  onClose: () => void;
  petshop: PetshopId;
  producto: PetshopProducto | null;
}

export function ProductoPetshopSheet({ visible, onClose, petshop, producto }: Props) {
  const { metaDe } = usePetshopsMeta();
  const [nombre, setNombre] = useState('');
  const [categoria, setCategoria] = useState('');
  const [precio, setPrecio] = useState('');
  const [stock, setStock] = useState('');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setNombre(producto?.nombre ?? '');
    setCategoria(producto?.categoria ?? '');
    setPrecio(producto ? String(producto.precio) : '');
    setStock(producto ? String(producto.stock ?? 0) : '');
    setNotas(producto?.notas ?? '');
    setError(null);
    setSaving(false);
  }, [visible, producto]);

  const onSave = async () => {
    setError(null);
    if (!nombre.trim()) return setError('Nombre obligatorio.');
    if (!categoria.trim()) return setError('Categoría obligatoria.');
    const p = Number(precio.replace(',', '.'));
    if (Number.isNaN(p) || p <= 0) return setError('Precio inválido.');
    const st = stock.trim() === '' ? 0 : Number(stock.replace(',', '.'));
    if (Number.isNaN(st) || st < 0) return setError('Stock inválido.');
    setSaving(true);
    try {
      await upsertProductoPetshop({
        id: producto?.id,
        petshop,
        nombre: nombre.trim(),
        categoria: categoria.trim(),
        precio: p,
        stock: st,
        notas: notas.trim() || undefined,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="md"
      title={producto ? 'Editar producto' : 'Nuevo producto'}
      subtitle={metaDe(petshop).nombre}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button label={producto ? 'Guardar cambios' : 'Crear producto'} onPress={onSave} loading={saving} />
        </>
      }
    >
      <TextField label="Nombre" placeholder="Collar gatito negro" value={nombre} onChangeText={setNombre} />
      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
        <TextField containerStyle={{ flex: 1, minWidth: 140 }} label="Precio (Bs)" placeholder="0.00" value={precio} onChangeText={setPrecio} keyboardType="decimal-pad" />
        <TextField containerStyle={{ flex: 1, minWidth: 140 }} label="Stock" placeholder="0" value={stock} onChangeText={setStock} keyboardType="number-pad" />
      </View>
      <TextField label="Categoría" placeholder="Collares · Juguetes · Snacks · Arnés…" value={categoria} onChangeText={setCategoria} />
      <TextField label="Notas (opcional)" placeholder="Stock estimado, talla, color…" value={notas} onChangeText={setNotas} multiline />

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
